import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { getMilestoneMessage } from '../utils/streakUtils';
import { getDayOfYear } from '../utils/planUtils';

interface CompletionDialogProps {
  open: boolean;
  onClose: () => void;
  streak: number;
  progress: number;
  totalDays: number;
  congratulationsMessage: {
    verseContent: string;
    reference?: string | undefined;
  };
}

const CompletionDialog: React.FC<CompletionDialogProps> = ({
  open,
  onClose,
  streak,
  progress,
  totalDays,
  congratulationsMessage,
}) => {
  const progressPercentage =
    progress > 0 ? Math.max(1, Math.round((progress / totalDays) * 100)) : 0;

  // Calculate what day of the plan the user should be on
  const currentDayOfYear = getDayOfYear(new Date());
  const expectedProgressPercentage = Math.round(
    (currentDayOfYear / totalDays) * 100
  );

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: 'white',
          color: 'black',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography
          variant='h5'
          component='div'
          fontWeight='bold'
          color='primary'
        >
          Great Job!
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
        {/* Congratulations Verse */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Box sx={{ fontStyle: 'italic', mb: 1 }}>
            <div
              dangerouslySetInnerHTML={{
                __html: congratulationsMessage.verseContent,
              }}
            />
          </Box>
          {congratulationsMessage.reference && (
            <Typography variant='caption' color='text.secondary'>
              ({congratulationsMessage.reference})
            </Typography>
          )}
        </Box>

        {/* Streak Section */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant='h6' gutterBottom color='primary'>
            Current Streak
          </Typography>
          <Typography variant='h3' fontWeight='bold' color='primary.main'>
            {streak} {streak === 1 ? 'Day' : 'Days'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Keep it going!
          </Typography>
        </Box>

        {/* Progress Section */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Typography variant='h6' gutterBottom color='primary'>
            Overall Progress
          </Typography>
          <Typography variant='body1' sx={{ mb: 1 }}>
            {progress} of {totalDays} days completed
          </Typography>
          <Box sx={{ width: '100%', mb: 1 }}>
            <Tooltip
              title={
                progressPercentage < expectedProgressPercentage
                  ? `You're ${
                      expectedProgressPercentage - progressPercentage
                    }% behind schedule. Today is day ${currentDayOfYear} of the year, so you should be at ${expectedProgressPercentage}% of your reading plan.`
                  : progressPercentage > expectedProgressPercentage
                  ? `Great job! You're ${
                      progressPercentage - expectedProgressPercentage
                    }% ahead of schedule.`
                  : `Perfect! You're right on schedule for day ${currentDayOfYear}.`
              }
              arrow
            >
              <LinearProgress
                variant='determinate'
                value={progressPercentage}
                valueBuffer={expectedProgressPercentage}
                color='primary'
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Tooltip>
          </Box>
          <Typography variant='body2' color='text.secondary'>
            {progressPercentage}% of your reading plan
          </Typography>
          {expectedProgressPercentage > progressPercentage && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ mt: 0.5 }}
            >
              Target: {expectedProgressPercentage}% (Day {currentDayOfYear})
            </Typography>
          )}
        </Box>

        {/* Milestone Message */}
        {getMilestoneMessage(streak, progress, totalDays) && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: 'primary.light',
              borderRadius: 1,
              opacity: 0.9,
            }}
          >
            <Typography
              variant='body1'
              fontWeight='bold'
              color='primary.contrastText'
            >
              {getMilestoneMessage(streak, progress, totalDays)}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={onClose}
          variant='contained'
          color='primary'
          size='large'
        >
          Continue Reading
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompletionDialog;
